import { type IntroCourseCMS } from "@repo/common/src/types/firestore/cms/introCourse";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  type EntityReference,
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
  type EntityOnSaveProps,
} from "@firecms/core";

import { MAX_LESSON_DURATION_MINUTES } from "@repo/common";
import { standardPermissions } from "../../utils/constants";
import { fileOperationsIntroCourse } from "../../utils/fileOperationsIntroCourse";
import { generateUniqueSlug } from "../../utils/generateUniqueSlug";
import { loadingOverlay } from "../../utils/loadOverlay";
import { Groups } from "../types";

type IntroCourseCMSModel = Omit<IntroCourseCMS, "author"> & { author: EntityReference };
type IntroCourseCMSFormModel = Omit<IntroCourseCMSModel, "created_on" | "updated_on"> & {
  created_on: Date;
  updated_on: Date;
};

const callbacks = buildEntityCallbacks({
  onPreSave: async (props: EntityOnSaveProps<IntroCourseCMSFormModel>) => {
    const { values, entityId } = props;

    loadingOverlay.show();

    if (values.name) {
      const baseSlug = values.slug || values.name;
      values.slug = await generateUniqueSlug(getFirestore(), CMSCollections.IntroCourses, baseSlug, entityId);
    }

    if (!values.files) {
      return values;
    }

    const storage = getStorage();
    const db = getFirestore();

    // Fetch the original document to compare
    const originalData = entityId
      ? await getDoc(doc(db, CMSCollections.IntroCourses, entityId)).then(
          (docSnap) => docSnap.data() as IntroCourseCMS | undefined,
        )
      : undefined;

    const updatedFiles = await Promise.all(
      values.files.map(async (file: IntroCourseCMS["files"][number], index: number) => {
        const originalFile = originalData?.files[index];
        try {
          return await fileOperationsIntroCourse({
            file,
            originalFile,
            storage,
          });
        } catch (error: unknown) {
          const msg = error instanceof Error ? error.message : String(error);
          if (msg.includes("MAX_LESSON_DURATION_MINUTES")) {
            throw new Error(
              `Audio ist zu lang. Maximal erlaubt: ${String(MAX_LESSON_DURATION_MINUTES)} Minuten. Bitte kürzen und erneut hochladen.`,
            );
          }
          throw new Error("Audio konnte nicht verarbeitet werden. Bitte Datei prüfen und erneut versuchen.");
        }
      }),
    );

    return { ...values, files: updatedFiles };
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

export const introCourses = buildCollection<IntroCourseCMSFormModel>({
  id: CMSCollections.IntroCourses,
  name: "Intro Kurse",
  singularName: "Intro Kurs",
  description: "Kann nur nach manueller freischaltung geändert werden, da Absprache mit Entwicklern notwendig ist.",
  path: CMSCollections.IntroCourses,
  icon: "Audiotrack",
  group: Groups.Courses,
  permissions: () => {
    return standardPermissions;
  },
  callbacks,
  subcollections: [],
  properties: {
    name: {
      name: "Name",
      validation: { required: true },
      dataType: "string",
    },
    slug: {
      name: "URL-freundlicher Slug. Wird automatisch generiert, wenn leer.",
      dataType: "string",
    },
    main_image: buildProperty({
      name: "Kursbild",
      description: "Bild des Kurses. Bitte in 1024x1024 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.IntroCoursesImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxHeight: 1024,
          maxWidth: 1024,
        },
      },
    }),
    description: {
      name: "Beschreibung",
      multiline: true,
      dataType: "string",
      validation: { required: true },
      columnWidth: 300,
    },
    author: {
      dataType: "reference",
      name: "Autor",
      validation: { required: true },
      path: CMSCollections.Authors,
    },
    files: {
      name: "Audio",
      dataType: "array",
      validation: { required: true },
      of: {
        dataType: "map",
        properties: {
          name: {
            name: "Name",
            dataType: "string",
            validation: { required: true },
          },
          closing: {
            name: "Schlusstext",
            multiline: true,
            dataType: "string",
            validation: { required: true },
            columnWidth: 300,
          },
          file: {
            name: "File",
            dataType: "string",
            validation: { required: true, uniqueInArray: true },
            storage: {
              storagePath: StoragePaths.IntroCoursesFiles,
              acceptedFiles: ["audio/*"],
              maxSize: 100 * 1024 * 1024, // 100 MB in bytes
            },
          },
        },
      },
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
  },
});
